// ============================================================
// MANIC MEETER — Browser DOM Renderer
// ============================================================

import { GameState, Meeting, Player, ColleagueRole, MeetingType, AchievementId, BingoCard, EndingType, GameEnding } from "./types";
import { getMeetingConfig } from "./meetings";
import {
  achievementDefs, colleagueRoleLabel, dailyPerk, endings, meetingTypeLabel, playerRoleLabel,
  powerupDef,
} from "./content";
import { C } from "./i18n";

// Map colleague roles to neon CSS classes
const ROLE_NEON_CLASS: Record<ColleagueRole, string> = {
  [ColleagueRole.FrontendDev]: "neon-frontend-dev",
  [ColleagueRole.BackendDev]: "neon-backend-dev",
  [ColleagueRole.ClientManager]: "neon-client-manager",
  [ColleagueRole.HRManager]: "neon-hr-manager",
  [ColleagueRole.MarketingGuru]: "neon-marketing-guru",
  [ColleagueRole.Chef]: "neon-chef",
  [ColleagueRole.Abteilungsleiter]: "neon-abteilungsleiter",
  [ColleagueRole.Teamleiter]: "neon-teamleiter",
  [ColleagueRole.Designer]: "neon-designer",
  [ColleagueRole.Praktikant]: "neon-praktikant",
  [ColleagueRole.ScrumMaster]: "neon-scrum-master",
  [ColleagueRole.ProductOwner]: "neon-product-owner",
  [ColleagueRole.Sprachbarriere]: "neon-sprachbarriere",
};

export function getNeonClass(role: ColleagueRole): string {
  return ROLE_NEON_CLASS[role] ?? "";
}

// We keep references to key DOM elements
let gameContainer: HTMLElement;
let statusBar: HTMLElement;
let meetingPanel: HTMLElement;
let logPanel: HTMLElement;
let actionPanel: HTMLElement;
let participantPanel: HTMLElement;
let infoPanel: HTMLElement;
let activeBonusesBar: HTMLElement;

export function initRenderer(): void {
  gameContainer = document.getElementById("game")!;
  statusBar = document.getElementById("status-bar")!;
  meetingPanel = document.getElementById("meeting-panel")!;
  logPanel = document.getElementById("log-panel")!;
  actionPanel = document.getElementById("action-panel")!;
  participantPanel = document.getElementById("participant-panel")!;
  infoPanel = document.getElementById("info-panel")!;
  activeBonusesBar = document.getElementById("active-bonuses-bar")!;

  // Info panel toggle
  const toggleBtn = document.getElementById("info-toggle-btn");
  if (toggleBtn) {
    toggleBtn.addEventListener("click", () => {
      const collapsed = infoPanel.classList.toggle("collapsed");
      toggleBtn.textContent = collapsed ? C().ui.infoPanel.toggleClosed : C().ui.infoPanel.toggleOpen;
    });
  }
}

export function updateInfoPanel(state: GameState): void {
  if (!infoPanel) return;

  const ui = C().ui;

  // Active perks — with tooltip descriptions
  let perksHtml = "<ul>";
  if (state.activePerks.length === 0) {
    perksHtml += `<li>${ui.infoPanel.noPerks}</li>`;
  } else {
    for (const id of state.activePerks) {
      const def = dailyPerk(id);
      if (def) {
        perksHtml += `<li class="${def.isPositive ? "positive" : "negative"} has-tooltip" data-tooltip="${def.description}">${def.emoji} ${def.name}</li>`;
      }
    }
  }
  perksHtml += "</ul>";

  // Active powerups — with tooltip descriptions
  let powerupsHtml = "<ul>";
  if (state.activePowerups.length === 0) {
    powerupsHtml += `<li>${ui.infoPanel.noPowerups}</li>`;
  } else {
    for (const ap of state.activePowerups) {
      const def = powerupDef(ap.id);
      if (def) {
        const turns = ap.remainingTurns === -1
          ? ui.infoPanel.wholeMeeting
          : ui.infoPanel.turnsLeft(ap.remainingTurns);
        powerupsHtml += `<li class="positive has-tooltip" data-tooltip="${def.description} — ${turns} ${ui.infoPanel.remaining}">${def.name} (${turns})</li>`;
      }
    }
  }
  powerupsHtml += "</ul>";

  // Quick stats
  const ss = state.sessionStats;
  const statsHtml = `<ul>
    <li>${ui.panels.statMeetings}: ${ss.totalMeetingsSurvived}</li>
    <li>${ui.panels.statCaught}: ${ss.totalTimesCaught}x</li>
    <li>${ui.panels.statFellAsleep}: ${ss.totalTimesFellAsleep}x</li>
    <li>${ui.panels.statMinigames}: ${ss.totalMinigamesWon}W / ${ss.totalMinigamesLost}L</li>
    <li>${ui.panels.statProcrastinated}: ${ss.totalSuccessfulProcrastinations}/${ss.totalProcrastinations}</li>
  </ul>`;

  infoPanel.innerHTML = `
    <div class="info-panel-grid">
      <div class="info-section"><h4>${ui.infoPanel.perksHeading}</h4>${perksHtml}</div>
      <div class="info-section"><h4>${ui.infoPanel.powerupsHeading}</h4>${powerupsHtml}</div>
      <div class="info-section"><h4>${ui.infoPanel.statsHeading}</h4>${statsHtml}</div>
    </div>
  `;

  // Also update the always-visible active bonuses bar
  updateActiveBonuses(state);
}

export function updateActiveBonuses(state: GameState): void {
  if (!activeBonusesBar) return;
  const items: string[] = [];
  const ui = C().ui;

  // Active perks
  for (const id of state.activePerks) {
    const def = dailyPerk(id);
    if (def) {
      items.push(`<span class="bonus-pill ${def.isPositive ? "bonus-positive" : "bonus-negative"}" data-tooltip="${def.description}">${def.emoji} ${def.name}</span>`);
    }
  }

  // Active powerups
  for (const ap of state.activePowerups) {
    const def = powerupDef(ap.id);
    if (def) {
      const turns = ap.remainingTurns === -1
        ? ui.infoPanel.wholeMeeting
        : ui.infoPanel.turnsShort(ap.remainingTurns);
      items.push(`<span class="bonus-pill bonus-powerup" data-tooltip="${def.description}">${def.name} [${turns}]</span>`);
    }
  }

  // Attest
  if (state.attestDaysRemaining > 0) {
    items.push(`<span class="bonus-pill bonus-special" data-tooltip="${ui.panels.attestTooltip(state.attestDaysRemaining)}">${ui.panels.attestPill(state.attestDaysRemaining)}</span>`);
  }

  if (items.length === 0) {
    activeBonusesBar.style.display = "none";
  } else {
    activeBonusesBar.style.display = "flex";
    activeBonusesBar.innerHTML = items.join("");
  }
}

export function getActionPanel(): HTMLElement {
  return actionPanel;
}

export function showScreen(screenId: string): void {
  document.querySelectorAll(".screen").forEach((el) => el.classList.remove("active"));
  document.getElementById(screenId)?.classList.add("active");
}

export function updateStatusBar(player: Player, impostorPoints: number = 0): void {
  const fatiguePercent = Math.round((player.fatigue / player.maxFatigue) * 100);
  let fatigueClass = "low";
  if (fatiguePercent >= 70) fatigueClass = "critical";
  else if (fatiguePercent >= 40) fatigueClass = "medium";

  const salaryClass = player.salary < 1500 ? "critical" : player.salary < 2000 ? "medium" : "";

  const prevPercent = lastFatiguePercent;

  statusBar.innerHTML = `
    <div class="stat">
      <span class="stat-label">👤</span>
      <span class="stat-value">${player.name} (${playerRoleLabel(player.role)})</span>
    </div>
    <div class="stat fatigue-stat">
      <span class="stat-label">😴 ${C().ui.status.fatigue}</span>
      <div class="fatigue-bar-outer">
        <div class="fatigue-bar-inner ${fatigueClass}" style="width: ${prevPercent}%"></div>
      </div>
      <span class="stat-value">${player.fatigue}/${player.maxFatigue}</span>
    </div>
    <div class="stat">
      <span class="stat-label">💰 ${C().ui.status.salary}</span>
      <span class="stat-value ${salaryClass}">${player.salary}€</span>
    </div>
    <div class="stat">
      <span class="stat-label">🎭 IP</span>
      <span class="stat-value">${impostorPoints}</span>
    </div>
    <div class="stat">
      <span class="stat-label">📊 ${C().ui.status.day}</span>
      <span class="stat-value">${player.daysSurvived + 1}</span>
    </div>
    <div class="stat">
      <span class="stat-label">🏆 ${C().ui.status.meetings}</span>
      <span class="stat-value">${player.meetingsSurvived}</span>
    </div>
  `;

  // Animate fatigue bar from previous to current value
  if (prevPercent !== fatiguePercent) {
    animateFatigueBar(prevPercent, fatiguePercent);
  } else {
    lastFatiguePercent = fatiguePercent;
  }
}

export function showMeetingHeader(meeting: Meeting): void {
  const config = getMeetingConfig(meeting.type);
  const ui = C().ui;
  const onlineTag = meeting.isOnline
    ? `<span class="online-tag">${ui.panels.online}</span>`
    : `<span class="offline-tag">${ui.panels.offline}</span>`;
  const scene = getMeetingScene(meeting.type);
  meetingPanel.innerHTML = `
    <div class="meeting-header">
      ${scene}
      <h2>📅 ${meetingTypeLabel(meeting.type)} ${onlineTag}</h2>
      <p class="meeting-desc">${config.description}</p>
      <div class="meeting-meta">
        <span>${ui.meeting.participants(meeting.participants.length)}</span>
        <span>${ui.panels.round(meeting.roundsTotal - meeting.roundsRemaining + 1, meeting.roundsTotal)}</span>
        <span>${meeting.directionForward ? ui.meeting.directionForward : ui.meeting.directionBackward}</span>
      </div>
    </div>
  `;
}

export function updateMeetingMeta(meeting: Meeting): void {
  const metaEl = meetingPanel.querySelector(".meeting-meta");
  if (metaEl) {
    const ui = C().ui;
    metaEl.innerHTML = `
      <span>${ui.meeting.participants(meeting.participants.length)}</span>
      <span>${ui.panels.round(meeting.roundsTotal - meeting.roundsRemaining + 1, meeting.roundsTotal + (meeting.roundsRemaining < 0 ? -meeting.roundsRemaining : 0))}</span>
      <span>${meeting.directionForward ? ui.meeting.directionForward : ui.meeting.directionBackward}</span>
    `;
  }
}

export function renderParticipants(meeting: Meeting, highlightSlot: number): void {
  let html = '<div class="participant-list">';
  for (let i = 0; i < meeting.turnOrder.length; i++) {
    const slot = meeting.turnOrder[i];
    const isActive = i === meeting.currentSpeakerIdx;
    const isHighlight = slot === highlightSlot;
    const isPlayer = slot === meeting.playerSlot;

    let cls = "participant";
    if (isActive) cls += " speaking";
    if (isHighlight) cls += " highlight";
    if (isPlayer) cls += " player";

    if (isPlayer) {
      html += `<div class="${cls}"><span class="p-name">${C().ui.meeting.you}</span></div>`;
    } else {
      const col = meeting.participants[slot];
      const neonCls = ROLE_NEON_CLASS[col.role] ?? "";
      const portrait = getPortrait(col.role);
      const talkDots = "🗣".repeat(Math.min(Math.ceil(col.talkiness / 2), 5));
      const foreign = col.role === ColleagueRole.Sprachbarriere ? C().foreignColleague : null;
      const roleTag = foreign
        ? `<span class="p-role has-tooltip" data-tooltip="${foreign.tooltip}">🌍 ${foreign.roleLabel} · ${foreign.tenure}</span>`
        : `<span class="p-role">${colleagueRoleLabel(col.role)}</span>`;
      html += `<div class="${cls}">
        ${portrait}
        <span class="p-name ${neonCls}">${col.name}</span>
        ${roleTag}
        <span class="p-talk">${talkDots}</span>
      </div>`;
    }
  }
  html += "</div>";
  participantPanel.innerHTML = html;

  // Collapse participant panel for compactness if too many
  if (meeting.turnOrder.length > 8) {
    participantPanel.classList.add("compact");
  } else {
    participantPanel.classList.remove("compact");
  }
}

export function addLogEntry(message: string, type: "normal" | "warning" | "danger" | "success" | "flavor" | "address" = "normal"): void {
  const entry = document.createElement("div");
  entry.className = `log-entry ${type}`;
  entry.innerHTML = message;
  logPanel.appendChild(entry);
  logPanel.scrollTop = logPanel.scrollHeight;
}

export function clearLog(): void {
  logPanel.innerHTML = "";
}

export function clearActionPanel(): void {
  actionPanel.innerHTML = "";
}

export function showGameOver(state: GameState): void {
  showScreen("game-screen");
  const ui = C().ui;
  const endingTable = endings();
  const ending: GameEnding | null = state.ending ? endingTable[state.ending] : null;
  const title = ending ? `${ending.emoji} ${ending.title} ${ending.emoji}` : ui.gameOver.heading;
  const desc = ending ? ending.description : state.gameOverReason;
  const bonusPts = ending ? ending.bonusPoints : 0;
  meetingPanel.innerHTML = `
    <div class="game-over">
      ${ASCII_GAME_OVER[0]}
      <h1>${title}</h1>
      <p>${desc}</p>
      ${bonusPts > 0 ? `<p class="ending-bonus">${ui.gameOver.bonusPoints(bonusPts)}</p>` : ""}
      <div class="final-stats">
        <p>${ui.panels.finalDaysSurvived}: <strong>${state.player.daysSurvived}</strong></p>
        <p>${ui.panels.finalMeetingsSurvived}: <strong>${state.player.meetingsSurvived}</strong></p>
        <p>${ui.panels.finalPeakSalary}: <strong>${state.peakSalary}€</strong></p>
      </div>
      ${state.meta.endingsUnlocked.length > 0 ? `
        <div class="endings-collection">
          <h3>${ui.panels.endingsCollection}</h3>
          <div class="endings-grid">
            ${Object.values(EndingType).map((e) => {
              const def = endingTable[e];
              const unlocked = state.meta.endingsUnlocked.includes(e);
              const unknown = ui.gameOver.unknownEnding;
              return `<span class="ending-badge ${unlocked ? "unlocked" : "locked"}" title="${unlocked ? def.description : unknown}">${unlocked ? def.emoji : "❓"} ${unlocked ? def.title : unknown}</span>`;
            }).join("")}
          </div>
        </div>
      ` : ""}
    </div>
  `;
  participantPanel.innerHTML = "";
  const replayBtn = document.createElement("button");
  replayBtn.className = "choice-btn continue";
  replayBtn.textContent = ui.gameOver.playAgain;
  replayBtn.addEventListener("click", () => location.reload());
  actionPanel.innerHTML = "";
  actionPanel.appendChild(replayBtn);
}

export function showDaySummary(state: GameState): void {
  const ds = state.dayStats;
  const salaryDelta = ds.salaryEnd - ds.salaryStart;
  const salaryDeltaStr = salaryDelta >= 0 ? `+${salaryDelta}€` : `${salaryDelta}€`;
  const salaryClass = salaryDelta >= 0 ? "success" : "danger";
  const ui = C().ui;
  meetingPanel.innerHTML = `
    <div class="day-summary">
      <h2>${ui.day.daySurvived(state.currentDay)}</h2>
      <table class="stats-table">
        <tr><td>${ui.panels.summaryMeetingsSurvived}</td><td><strong>${ds.meetingsSurvived}</strong></td></tr>
        <tr><td>${ui.panels.summaryMeetingsSkipped}</td><td>${ds.meetingsSkipped}</td></tr>
        <tr><td>${ui.panels.summaryTimesCaught}</td><td>${ds.timesCaught}</td></tr>
        <tr><td>${ui.panels.summaryTimesFellAsleep}</td><td>${ds.timesFellAsleep}</td></tr>
        <tr><td>${ui.panels.summaryMinigamesWon}</td><td>${ds.minigamesWon}</td></tr>
        <tr><td>${ui.panels.summaryMinigamesLost}</td><td>${ds.minigamesLost}</td></tr>
        <tr><td>${ui.panels.summarySalary}</td><td>${state.player.salary}€ (<span class="${salaryClass}">${salaryDeltaStr}</span>) | ${ui.panels.summaryRecord}: ${state.peakSalary}€</td></tr>
        <tr><td>${ui.panels.summaryFatigue}</td><td>${state.player.fatigue}/${state.player.maxFatigue}</td></tr>
      </table>
    </div>
  `;
  participantPanel.innerHTML = "";
}

// ---- ASCII Bar Chart Helper ----

function asciiBar(value: number, max: number, width: number = 20): string {
  if (max <= 0) return "░".repeat(width);
  const filled = Math.round((value / max) * width);
  return "█".repeat(Math.min(filled, width)) + "░".repeat(Math.max(0, width - filled));
}

// ---- Enhanced Day Summary with Bar Charts ----

export function showDaySummaryWithCharts(state: GameState): void {
  const ds = state.dayStats;
  const salaryDelta = ds.salaryEnd - ds.salaryStart;
  const salaryDeltaStr = salaryDelta >= 0 ? `+${salaryDelta}€` : `${salaryDelta}€`;
  const salaryClass = salaryDelta >= 0 ? "success" : "danger";

  // Build meeting type breakdown
  const meetingTypeCounts: Record<string, number> = {};
  for (const mt of ds.meetingTypesThisDay) {
    meetingTypeCounts[mt] = (meetingTypeCounts[mt] || 0) + 1;
  }
  const maxMeetingCount = Math.max(1, ...Object.values(meetingTypeCounts));
  let meetingTypeChart = "";
  for (const [type, count] of Object.entries(meetingTypeCounts)) {
    meetingTypeChart += `<div class="bar-row"><span class="bar-label">${meetingTypeLabel(type as MeetingType)}</span><span class="bar-chart">${asciiBar(count, maxMeetingCount, 15)}</span><span class="bar-value">${count}</span></div>`;
  }

  // Build colleague fatigue chart (top 5 most fatiguing roles today from session)
  const ss = state.sessionStats;
  if (!ss.colleagueRoleStats) ss.colleagueRoleStats = {};
  if (!ss.meetingTypeStats) ss.meetingTypeStats = {};
  const roleEntries = Object.entries(ss.colleagueRoleStats)
    .sort((a, b) => b[1].fatigueCaused - a[1].fatigueCaused)
    .slice(0, 5);
  const maxFatigue = roleEntries.length > 0 ? Math.max(1, roleEntries[0][1].fatigueCaused) : 1;
  let colleagueChart = "";
  for (const [role, data] of roleEntries) {
    colleagueChart += `<div class="bar-row"><span class="bar-label">${colleagueRoleLabel(role as ColleagueRole)}</span><span class="bar-chart">${asciiBar(data.fatigueCaused, maxFatigue, 15)}</span><span class="bar-value">${data.fatigueCaused} 😴</span></div>`;
  }

  // Build meeting deadliness chart (from session stats)
  const typeDeadliness = Object.entries(ss.meetingTypeStats)
    .map(([type, data]) => ({
      type,
      deaths: data.fellAsleep + data.caught,
      survived: data.survived,
    }))
    .filter((e) => e.deaths > 0 || e.survived > 0)
    .sort((a, b) => b.deaths - a.deaths)
    .slice(0, 5);
  const maxDeadly = typeDeadliness.length > 0 ? Math.max(1, typeDeadliness[0].deaths) : 1;
  let deadlinessChart = "";
  for (const entry of typeDeadliness) {
    deadlinessChart += `<div class="bar-row"><span class="bar-label">${meetingTypeLabel(entry.type as MeetingType)}</span><span class="bar-chart">${asciiBar(entry.deaths, maxDeadly, 15)}</span><span class="bar-value">${entry.deaths} 💀 / ${entry.survived} ✅</span></div>`;
  }

  // Build achievements summary
  const ui = C().ui;
  const achievements = achievementDefs();
  const unlockedCount = state.achievements.size;
  const totalCount = achievements.length;
  let achievementHtml = `<div class="achievement-summary"><strong>${ui.achievements.unlockedOf(unlockedCount, totalCount)}</strong></div>`;
  achievementHtml += '<div class="achievement-grid">';
  for (const def of achievements) {
    const unlocked = state.achievements.has(def.id);
    achievementHtml += `<div class="achievement-item ${unlocked ? "unlocked" : "locked"}" title="${def.description}"><span class="achievement-emoji">${unlocked ? def.emoji : ui.achievements.locked}</span><span class="achievement-name">${def.name}</span></div>`;
  }
  achievementHtml += "</div>";

  meetingPanel.innerHTML = `
    <div class="day-summary day-summary-extended">
      <h2>${ui.day.daySurvived(state.currentDay)}</h2>
      <table class="stats-table">
        <tr><td>${ui.panels.summaryMeetingsSurvived}</td><td><strong>${ds.meetingsSurvived}</strong></td></tr>
        <tr><td>${ui.panels.summaryMeetingsSkipped}</td><td>${ds.meetingsSkipped}</td></tr>
        <tr><td>${ui.panels.summaryTimesCaught}</td><td>${ds.timesCaught}</td></tr>
        <tr><td>${ui.panels.summaryTimesFellAsleep}</td><td>${ds.timesFellAsleep}</td></tr>
        <tr><td>${ui.panels.summaryMonologues}</td><td>${ds.monologues}</td></tr>
        <tr><td>${ui.panels.summaryMinigamesWon}</td><td>${ds.minigamesWon}</td></tr>
        <tr><td>${ui.panels.summaryMinigamesLost}</td><td>${ds.minigamesLost}</td></tr>
        <tr><td>${ui.panels.summarySalary}</td><td>${state.player.salary}€ (<span class="${salaryClass}">${salaryDeltaStr}</span>) | ${ui.panels.summaryRecord}: ${state.peakSalary}€</td></tr>
        <tr><td>${ui.panels.summaryFatigue}</td><td>${state.player.fatigue}/${state.player.maxFatigue}</td></tr>
      </table>

      ${meetingTypeChart ? `<div class="chart-section"><h3>${ui.day.chartMeetingsToday}</h3><div class="bar-chart-container">${meetingTypeChart}</div></div>` : ""}
      ${colleagueChart ? `<div class="chart-section"><h3>${ui.day.chartFatigueCausers}</h3><div class="bar-chart-container">${colleagueChart}</div></div>` : ""}
      ${deadlinessChart ? `<div class="chart-section"><h3>${ui.day.chartDeadliest}</h3><div class="bar-chart-container">${deadlinessChart}</div></div>` : ""}
      <div class="chart-section">${achievementHtml}</div>
    </div>
  `;
  participantPanel.innerHTML = "";
}

// ---- Achievement Progress Display ----

export function showAchievementProgress(achievements: Set<AchievementId>): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.id = "achievement-modal";

  const ui = C().ui;
  const defs = achievementDefs();
  const unlockedCount = achievements.size;
  const totalCount = defs.length;

  let gridHtml = "";
  for (const def of defs) {
    const unlocked = achievements.has(def.id);
    gridHtml += `
      <div class="achievement-card ${unlocked ? "unlocked" : "locked"}">
        <div class="achievement-card-emoji">${unlocked ? def.emoji : ui.achievements.locked}</div>
        <div class="achievement-card-name">${def.name}</div>
        <div class="achievement-card-desc">${def.description}</div>
      </div>`;
  }

  overlay.innerHTML = `
    <div class="modal-box achievement-modal-box">
      <h2>${ui.panels.achievementsModalHeading(unlockedCount, totalCount)}</h2>
      <div class="achievement-progress-bar">
        <div class="achievement-progress-fill" style="width: ${Math.round((unlockedCount / totalCount) * 100)}%"></div>
      </div>
      <div class="achievement-card-grid">${gridHtml}</div>
      <button class="choice-btn continue" id="achievement-close" style="margin-top:1rem">${ui.actions.close}</button>
    </div>
  `;
  document.body.appendChild(overlay);
  document.getElementById("achievement-close")!.addEventListener("click", () => {
    overlay.remove();
  }, { once: true });
}

export function showNewDay(day: number, schedule: string[]): void {
  const ui = C().ui;
  meetingPanel.innerHTML = `
    <div class="new-day">
      <h2>${ui.panels.newDayHeading(String(day).padStart(2, "0"))}</h2>
      <p>${ui.panels.todaysPlan}</p>
      <ul class="schedule-list">
        ${schedule.map((m, i) => `<li>${i + 1}. ${m}</li>`).join("")}
      </ul>
    </div>
  `;
  participantPanel.innerHTML = "";
}

export function showPrepPhase(meeting: Meeting): void {
  const config = getMeetingConfig(meeting.type);
  const ui = C().ui;
  meetingPanel.innerHTML = `
    <div class="prep-phase">
      <h2>${ui.panels.prepHeading}</h2>
      <p class="meeting-desc">${ui.panels.prepNextMeeting(meetingTypeLabel(meeting.type))}</p>
      <p class="meeting-desc">${config.description}</p>
      <p class="prep-hint">${ui.panels.prepHint}</p>
    </div>
  `;
  participantPanel.innerHTML = "";
}

export function showMeetingProgress(schedule: string[], currentIndex: number): void {
  let html = '<div class="meeting-minimap">';
  for (let i = 0; i < schedule.length; i++) {
    let cls = "minimap-item";
    if (i < currentIndex) cls += " done";
    else if (i === currentIndex) cls += " current";
    const icon = i < currentIndex ? "✅" : i === currentIndex ? "▶️" : "⬜";
    html += `<span class="${cls}">${icon} ${schedule[i]}</span>`;
  }
  html += "</div>";
  const el = document.getElementById("meeting-progress");
  if (el) {
    el.innerHTML = html;
  } else {
    // Insert above the log panel
    const container = document.createElement("div");
    container.id = "meeting-progress";
    container.innerHTML = html;
    logPanel.parentElement?.insertBefore(container, logPanel);
  }
}

export function clearMeetingProgress(): void {
  const el = document.getElementById("meeting-progress");
  if (el) el.innerHTML = "";
}

// ============================================================
// ASCII ART — Animated scenes for sleeping, caught, game over
// ============================================================

const ASCII_SLEEPING = [
  `<pre class="ascii-art ascii-sleep">
    ___
   / _ \\    Z Z Z
  | (_) |  z z z
   \\___/  z z
   /|  |\\
  (_|  |_)  💤
  </pre>`,
  `<pre class="ascii-art ascii-sleep">
    ___
   / _ \\     Z Z Z
  | (_) |   z z z
   \\___/   z z
   /|  |\\
  (_|  |_)   💤💤
  </pre>`,
  `<pre class="ascii-art ascii-sleep">
    ___
   / _ \\      Z Z Z
  | (-_) |    z z z
   \\___/    z z z
   /|  |\\
  (_|  |_)    💤💤💤
  </pre>`,
];

const ASCII_CAUGHT = `<pre class="ascii-art ascii-caught">
   +===========+
   |  !!!      |
   +-----------+
   |  ERWISCHT |
   |   beim    |
   | Prokrasti-|
   |   nieren! |
   +===========+
</pre>`;

const ASCII_GAME_OVER = [
  `<pre class="ascii-art ascii-gameover">
  +========================+
  |                        |
  |    ######   ######     |
  |   ##       ##    ##    |
  |   ##  ###  ##    ##    |
  |   ##   ##  ##    ##    |
  |    ######   ######     |
  |                        |
  |      GAME  OVER        |
  |                        |
  |     -- GEKUENDIGT --   |
  |                        |
  +========================+
</pre>`,
];

export function showSleepingAnimation(): Promise<void> {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    container.className = "ascii-animation-overlay";
    document.body.appendChild(container);
    let frame = 0;
    container.innerHTML = ASCII_SLEEPING[0];
    const interval = setInterval(() => {
      frame++;
      if (frame >= ASCII_SLEEPING.length * 2) {
        clearInterval(interval);
        container.classList.add("ascii-fade-out");
        setTimeout(() => { container.remove(); resolve(); }, 500);
        return;
      }
      container.innerHTML = ASCII_SLEEPING[frame % ASCII_SLEEPING.length];
    }, 400);
  });
}

export function showCaughtAnimation(): Promise<void> {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    container.className = "ascii-animation-overlay ascii-shake";
    container.innerHTML = ASCII_CAUGHT;
    document.body.appendChild(container);
    setTimeout(() => {
      container.classList.add("ascii-fade-out");
      setTimeout(() => { container.remove(); resolve(); }, 500);
    }, 1500);
  });
}

export function showGameOverAnimation(): Promise<void> {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    container.className = "ascii-animation-overlay ascii-gameover-anim";
    container.innerHTML = ASCII_GAME_OVER[0];
    document.body.appendChild(container);
    setTimeout(() => {
      container.classList.add("ascii-fade-out");
      setTimeout(() => { container.remove(); resolve(); }, 800);
    }, 2500);
  });
}

export function getMeetingScene(type: MeetingType): string {
  return C().meetingScenes[type] ?? "";
}

// ============================================================
// COLLEAGUE ASCII PORTRAITS — small avatars per role
// ============================================================

/** Emoji only — the tooltip title comes from the active locale. */
const ROLE_PORTRAIT_EMOJI: Record<ColleagueRole, string> = {
  [ColleagueRole.FrontendDev]: "👨‍💻",
  [ColleagueRole.BackendDev]: "🧑‍💻",
  [ColleagueRole.ClientManager]: "🤝",
  [ColleagueRole.HRManager]: "📋",
  [ColleagueRole.MarketingGuru]: "📢",
  [ColleagueRole.Chef]: "👔",
  [ColleagueRole.Abteilungsleiter]: "🏢",
  [ColleagueRole.Teamleiter]: "📊",
  [ColleagueRole.Designer]: "🎨",
  [ColleagueRole.Praktikant]: "🧑‍🎓",
  [ColleagueRole.ScrumMaster]: "🔄",
  [ColleagueRole.ProductOwner]: "📌",
  [ColleagueRole.Sprachbarriere]: "🌍",
};

export function getPortrait(role: ColleagueRole): string {
  const emoji = ROLE_PORTRAIT_EMOJI[role];
  if (!emoji) return "";
  return `<span class="portrait" title="${colleagueRoleLabel(role)}">${emoji}</span>`;
}

// ============================================================
// FATIGUE BAR ANIMATION — smooth fill/drain with CSS transition
// ============================================================

let lastFatiguePercent = 0;

export function animateFatigueBar(fromPercent: number, toPercent: number): void {
  const bar = document.querySelector(".fatigue-bar-inner") as HTMLElement;
  if (!bar) return;
  // Set start state without transition
  bar.style.transition = "none";
  bar.style.width = `${fromPercent}%`;
  // Force reflow
  void bar.offsetWidth;
  // Animate to target
  bar.style.transition = "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)";
  bar.style.width = `${toPercent}%`;
  lastFatiguePercent = toPercent;
}

export function getLastFatiguePercent(): number {
  return lastFatiguePercent;
}

// ============================================================
// SCREEN SHAKE EFFECT — for dramatic moments
// ============================================================

export function screenShake(intensity: "light" | "medium" | "heavy" = "medium"): void {
  const game = document.getElementById("game");
  if (!game) return;
  const cls = `shake-${intensity}`;
  game.classList.add(cls);
  const duration = intensity === "light" ? 300 : intensity === "medium" ? 500 : 800;
  setTimeout(() => game.classList.remove(cls), duration);
}

// ============================================================
// PROGRESS BAR FOR TYPING CHALLENGES
// ============================================================

export function createProgressBar(container: HTMLElement, total: number): {
  update: (current: number) => void;
  remove: () => void;
} {
  const wrapper = document.createElement("div");
  wrapper.className = "typing-progress-wrapper";
  const bar = document.createElement("div");
  bar.className = "typing-progress-bar";
  const fill = document.createElement("div");
  fill.className = "typing-progress-fill";
  const label = document.createElement("span");
  label.className = "typing-progress-label";
  label.textContent = `0/${total}`;
  bar.appendChild(fill);
  wrapper.appendChild(bar);
  wrapper.appendChild(label);
  container.appendChild(wrapper);

  return {
    update(current: number) {
      const pct = Math.round((current / total) * 100);
      fill.style.width = `${pct}%`;
      label.textContent = `${current}/${total}`;
      if (current >= total) fill.classList.add("typing-progress-complete");
    },
    remove() {
      if (wrapper.parentElement) wrapper.remove();
    },
  };
}

// ============================================================
// BINGO CARD DISPLAY
// ============================================================

export function renderBingoCard(card: BingoCard | null): void {
  let existing = document.getElementById("bingo-card-panel");
  if (!existing) {
    existing = document.createElement("div");
    existing.id = "bingo-card-panel";
    existing.className = "bingo-card-panel";
    const gameEl = document.getElementById("game");
    if (gameEl) gameEl.appendChild(existing);
  }
  if (!card) {
    existing.style.display = "none";
    return;
  }
  existing.style.display = "block";
  const completedCount = card.completed.filter(Boolean).length;
  const allDone = completedCount === card.goals.length;
  const ui = C().ui;
  existing.innerHTML = `
    <div class="bingo-card ${allDone ? "bingo-complete" : ""}">
      <div class="bingo-title">${ui.bingo.heading} ${allDone ? ui.bingo.bingoLine : ui.bingo.progress(completedCount, card.goals.length)}</div>
      <div class="bingo-goals">
        ${card.goals.map((g, i) => `
          <span class="bingo-goal ${card.completed[i] ? "done" : ""}">${g.emoji} ${C().bingoGoals[g.id] ?? g.id} ${card.completed[i] ? "✅" : "⬜"}</span>
        `).join("")}
      </div>
    </div>
  `;
}

// ============================================================
// WEEKEND EVENT DISPLAY
// ============================================================

export function showWeekendEvent(emoji: string, name: string, description: string): void {
  meetingPanel.innerHTML = `
    <div class="weekend-event-header">
      <h2>${emoji} ${name}</h2>
      <p class="meeting-desc">${description}</p>
      <p class="weekend-hint">${C().ui.panels.weekendHint}</p>
    </div>
  `;
}

// ============================================================
// SOUND TOGGLE BUTTON
// ============================================================

export function initSoundToggle(onToggle: (enabled: boolean) => void): void {
  const btn = document.getElementById("sound-toggle");
  if (!btn) return;

  // Restore saved preference
  const saved = localStorage.getItem("manic-meeter-sound");
  btn.textContent = saved === "0" ? C().ui.controls.soundOff : C().ui.controls.soundOn;
  if (saved === "0") onToggle(false);

  btn.addEventListener("click", () => {
    const current = btn.textContent?.includes("🔊");
    const newState = !current;
    btn.textContent = newState ? C().ui.controls.soundOn : C().ui.controls.soundOff;
    localStorage.setItem("manic-meeter-sound", newState ? "1" : "0");
    onToggle(newState);
  });
}
